import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import logger from "@/utils/logger";

let cachedUser: User | null = null;

export const ensureAuthenticated = async (): Promise<string> => {
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const { data } = await supabase.auth.getSession();
      const id = data.session?.user.id;
      if (id) {
        return id;
      }
    } catch (error) {
      logger.error("[sessionCache] Error fetching session:", error);
    }
    await new Promise((resolve) => setTimeout(resolve, 300));
  }
  throw new Error("No authenticated user");
};

export const setCachedUser = (user: User | null) => {
  cachedUser = user;
};

export const getCachedUser = async (): Promise<User | null> => {
  if (cachedUser) {
    return cachedUser;
  }

  try {
    const { data } = await supabase.auth.getSession();
    const user = data.session?.user || null;
    cachedUser = user;
    return user;
  } catch (error) {
    logger.error("[sessionCache] Error fetching session:", error);
    return null;
  }
};

export const getCachedUserId = async (): Promise<string | null> => {
  const user = await getCachedUser();
  return user?.id || null;
};

export const clearCachedUserId = () => {
  cachedUser = null;
};

// Refresh the session a bit before it actually expires, so queries fired right
// after the app returns to foreground don't race a stale token.
const SESSION_EXPIRY_SKEW_SECONDS = 120;

/**
 * True when an error is a "JWT expired" error (PostgREST PGRST303 or a message
 * mentioning JWT expiry). Used to distinguish a recoverable session error from
 * a generic failure.
 */
export const isJwtExpiredError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const { code, message } = error as { code?: string; message?: string };
  if (code === "PGRST303") {
    return true;
  }
  return typeof message === "string" && message.toLowerCase().includes("jwt expired");
};

/**
 * Ensures the Supabase session is valid, refreshing it proactively when it is
 * expired or about to expire. `getSession()` only returns the locally stored
 * session (which can be stale after a long background suspension on mobile),
 * so we force `refreshSession()` when needed instead of letting queries fail
 * with PGRST303 and cascade into generic errors.
 *
 * Returns `true` when a usable session is available afterwards, `false`
 * otherwise (e.g. offline, refresh failed, or no session at all). Never throws.
 */
export const ensureValidSession = async (): Promise<boolean> => {
  try {
    const { data } = await supabase.auth.getSession();
    const session = data.session;
    if (!session) {
      return false;
    }

    const expiresAt = session.expires_at;
    const nowSeconds = Math.floor(Date.now() / 1000);
    if (typeof expiresAt === "number" && expiresAt - nowSeconds > SESSION_EXPIRY_SKEW_SECONDS) {
      return true;
    }

    const { data: refreshed, error } = await supabase.auth.refreshSession();
    if (error || !refreshed.session) {
      return false;
    }
    if (refreshed.session.user) {
      cachedUser = refreshed.session.user;
    }
    return true;
  } catch (error) {
    logger.error("[sessionCache] Error ensuring valid session:", error);
    return false;
  }
};
